import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Col, Modal, ModalBody, Row } from "reactstrap";
import { css } from '@emotion/react';
import PndRestApi from "src/utils/pnd/PndRestApi";
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Divider, Box, IconButton } from '@mui/material';
import { showNotification } from "src/shared/components/Notification";
import Barcode from "react-barcode";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PrintIcon from '@mui/icons-material/Print';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoIcon from '@mui/icons-material/Info';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import html2canvas from 'html2canvas';
import { ERROR_MESSAGES } from "src/utils/pnd/PndConstants";
import { convertUTCToLocalFromDate, formatDate } from "src/utils/DateUtils";
import { getOrderPreferences } from "src/redux/actions/orderPreferencesActions";

const printStyles = css`
  @media print {
    .printContainer {
      width: 100%;
      height: 100%;
      display: block;
      background-color: white;
      color: black;
    }
    .printButton {
      display: none;
    }
  }
`;

const OrderDetailsShippingViewDetailsModal = ({ modalShow, setModalShow, dataInfo, isTitle }) => {
    const [isOrderItemDetails, setOrderItemDetails] = useState([]);
    const [barcodePngs, setBarcodePngs] = useState([]);
    const svgRefs = useRef([]);
    const orderPreferences = useSelector(state => state.orderPreferences);
    const dispatch = useDispatch();

    useEffect(() => {
        if (!orderPreferences?.data) {
            dispatch(getOrderPreferences());
        }
    }, [dispatch, orderPreferences]);


    useEffect(() => {
        if (modalShow && dataInfo?.orderId) {
            getOrderItemWiseDetails();
        }
    }, [modalShow, dataInfo]);

    useEffect(() => {
        if (isOrderItemDetails.length > 0) {
            fetchBarcodes();
        }
    }, [isOrderItemDetails]);

    const fetchBarcodes = async () => {
        try {
            const barcodes = await Promise.all(svgRefs.current.map(async (ref) => {
                if (!ref) return null;
                const canvas = await html2canvas(ref);
                return canvas.toDataURL('image/png');
            }));
            setBarcodePngs(barcodes.filter(Boolean));
        } catch (error) {
            console.error("Error generating barcodes:", error);
        }
    };

    const getOrderItemWiseDetails = async () => {
        try {
            const response = await PndRestApi.GetOrderItemWiseDetails(dataInfo?.orderId);
            if (response.status === 200 && response.data.status === 200) {
                setOrderItemDetails(response?.data?.data || []);
            } else {
                showNotification("theme-light", "ltr", `${response.status}`, response.data?.message || "Error fetching details");
            }
        } catch (error) {
            showNotification("theme-light", "ltr", "500", ERROR_MESSAGES.tryCatchError);
        }
    };

    const toggle = () => setModalShow(prevState => !prevState);

    const handlePrint = (item, index) => {
        const printWindow = window.open('', '_blank');
        const currentDate = new Date().toLocaleString();
        const order = isOrderItemDetails[0]?.orderInfo || {};

        const html = `
            <html>
                <head>
                    <title>Order Barcode Print - ${order.orderNo}</title>
                    <style>
                        body { font-family: 'Inter', sans-serif; margin: 40px; color: #333; }
                        .header { text-align: center; border-bottom: 2px solid #32CA73; padding-bottom: 10px; margin-bottom: 20px; }
                        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
                        .info-box { border: 1px solid #eee; padding: 15px; border-radius: 8px; }
                        .label { font-weight: bold; color: #666; font-size: 12px; text-transform: uppercase; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #eee; padding: 10px; text-align: left; font-size: 13px; }
                        th { background-color: #f9f9f9; }
                        .barcode-section { text-align: center; margin-top: 30px; padding: 20px; border: 2px dashed #eee; }
                        .footer { margin-top: 40px; font-size: 11px; color: #999; text-align: center; border-top: 1px solid #eee; padding-top: 10px; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2>Order Shipment Manifest</h2>
                        <p>Order No: <b>${order.orderNo}</b></p>
                    </div>
                    <div class="info-grid">
                        <div class="info-box">
                            <div class="label">Shipper / Pickup Agent</div>
                            <p>${dataInfo?.pickupAddress || 'N/A'}</p>
                        </div>
                        <div class="info-box">
                            <div class="label">Customer / Recipient</div>
                            <p>${dataInfo?.dropOffAddress || 'N/A'}<br>Contact: ${order.contactNo || 'N/A'}</p>
                        </div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Item Details</th>
                                <th>Weight</th>
                                <th>Dimensions</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>${item.boxBarCode} (Qty: ${item.noOfItems})</td>
                                <td>${item.weightKg || '--'} Kg</td>
                                <td>${item.boxDimension || '--'}</td>
                                <td>${item.boxDescription || '--'}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="barcode-section">
                        <img src="${barcodePngs[index]}" style="max-width: 450px; width: 100%;" />
                        <p style="margin-top: 10px; font-weight: bold; font-size: 16px;">${item.boxBarCode}</p>
                    </div>
                    <div class="footer">
                        Printed on ${currentDate} | Generated by PickNdrop.ae for Informap Technology Center
                    </div>
                </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
        setTimeout(() => { printWindow.print(); }, 500);
    };

    const order = isOrderItemDetails[0]?.orderInfo || {};

    const formatTime = (dateStr) => {
        const conversionRequired = orderPreferences?.data?.dateConversionRequired;
        if (conversionRequired) {
            return formatDate(dateStr) || '--';
        }
        return dateStr || '--';
    };

    return (
        <Modal
            isOpen={modalShow}
            toggle={toggle}
            className="theme-light ltr-support credit-memo-modal-form-ani"
            centered
            style={{ maxWidth: '1000px' }}
        >
            <div className="modal__header border-bottom-0 pb-0">
                <button
                    className="lnr lnr-cross modal__close-btn"
                    aria-label="modal__close-btn"
                    type="button"
                    onClick={toggle}
                />
                <div className="d-flex align-items-center">
                    <AssignmentIcon className="text-primary me-2" style={{ fontSize: '1.5rem' }} />
                    <h5 className="modal__title text-uppercase fw-bold m-0" style={{ color: "#32CA71", fontSize: '1.1rem' }}>
                        {isTitle || "Order Shipment Details"}
                        {order?.customerDeliveryTagMappingInfo?.customerDeliveryTagInfo?.tagName &&
                            <Box
                                component="span"
                                sx={{
                                    backgroundColor: order?.customerDeliveryTagMappingInfo?.customerDeliveryTagInfo?.colorCode || '#32CA71',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '12px',
                                    ml: 1,
                                    verticalAlign: 'middle',
                                    display: 'inline-block',
                                    fontWeight: 600,
                                    textTransform: 'none'
                                }}
                            >
                                {order?.customerDeliveryTagMappingInfo?.customerDeliveryTagInfo?.tagName}
                            </Box>
                        }
                    </h5>

                </div>
            </div>
            <ModalBody className="p-3">
                <Box className="printContainer" css={printStyles}>
                    {/* Header Info Card */}
                    <Paper elevation={0} className="p-3 mb-3 border rounded bg-light">
                        <Row className="g-2 text-center">
                            <Col md={3}>
                                <Typography className="text-muted small fw-bold text-uppercase">DLV No</Typography>
                                <Typography className="fw-bold">{order.orderNo || '--'}</Typography>
                            </Col>
                            <Col md={3} className="border-start">
                                <Typography className="text-muted small fw-bold text-uppercase">ERP Order</Typography>
                                <Typography className="fw-bold">{order.erpOrderNo || '--'}</Typography>
                            </Col>
                            <Col md={3} className="border-start">
                                <Typography className="text-muted small fw-bold text-uppercase">Sales (E-com)</Typography>
                                <Typography className="fw-bold">{order.salesOrderNo || '--'}</Typography>
                            </Col>

                            <Col md={3} className="border-start">
                                <Typography className="text-muted small fw-bold text-uppercase">Invoice No</Typography>
                                <Typography className="fw-bold">{order.invoiceNo || 'N/A'}</Typography>
                            </Col>
                        </Row>
                    </Paper>

                    {/* Address & Timeline Row */}
                    <Row className="g-3 mb-3">
                        <Col lg={4}>
                            <Paper elevation={0} className="p-3 h-100 border rounded">
                                <div className="d-flex align-items-center mb-2 text-success">
                                    <LocationOnIcon fontSize="small" className="me-1" />
                                    <Typography variant="subtitle2" className="fw-bold">Pickup Address</Typography>
                                </div>
                                <Typography variant="body2" className="text-muted" style={{ lineHeight: 1.6 }}>
                                    {dataInfo?.pickupAddress || "Not specified"}
                                </Typography>
                            </Paper>
                        </Col>
                        <Col lg={4}>
                            <Paper elevation={0} className="p-3 h-100 border rounded">
                                <div className="d-flex align-items-center mb-2 text-primary">
                                    <LocationOnIcon fontSize="small" className="me-1" />
                                    <Typography variant="subtitle2" className="fw-bold">Delivery Address</Typography>
                                </div>
                                <Typography variant="body2" className="text-muted" style={{ lineHeight: 1.6 }}>
                                    {dataInfo?.dropOffAddress || "Not specified"}
                                </Typography>
                                {order.contactNo && (
                                    <Chip label={`Contact: ${order.contactNo}`} size="small" className="mt-2" variant="outlined" />
                                )}
                            </Paper>
                        </Col>
                        <Col lg={4}>
                            <Paper elevation={0} className="p-3 h-100 border rounded">
                                <div className="d-flex align-items-center mb-2 text-warning">
                                    <CalendarTodayIcon fontSize="small" className="me-1" />
                                    <Typography variant="subtitle2" className="fw-bold">Execution Timeline</Typography>
                                </div>
                                <div className="small text-muted">
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>Requested:</span> <span className="fw-bold text-dark">{(order.requestedDateTimeSlot)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>Scheduled:</span> <span className="fw-bold text-dark">{convertUTCToLocalFromDate(order.scheduledDateTimeSlot)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>PGI:</span> <span className="fw-bold text-dark">{convertUTCToLocalFromDate(order.createdDateTime)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>PGR:</span> <span className="fw-bold text-dark">{convertUTCToLocalFromDate(order.goodsReceivedDatetime)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>Assigned:</span> <span className="fw-bold text-dark">{convertUTCToLocalFromDate(order.assignedDateTime)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>Dlv Start:</span> <span className="fw-bold text-dark">{convertUTCToLocalFromDate(order.startDateTime)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Completed:</span> <span className="fw-bold text-dark">{convertUTCToLocalFromDate(order.completedDateTime)}</span>
                                    </div>
                                </div>
                            </Paper>
                        </Col>
                    </Row>

                    {/* Items Table */}
                    <Typography variant="subtitle2" className="fw-bold mb-2 d-flex align-items-center">
                        <Box sx={{ width: 4, height: 16, bgcolor: 'primary.main', mr: 1, borderRadius: 1 }} />
                        Consolidated Item Details
                    </Typography>
                    <TableContainer component={Paper} elevation={0} className="border rounded mb-3 overflow-hidden">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableCell className="fw-bold py-2">Sl</TableCell>
                                    <TableCell className="fw-bold py-2">Box Barcode</TableCell>
                                    <TableCell className="fw-bold py-2">Qty</TableCell>
                                    <TableCell className="fw-bold py-2">Weight</TableCell>
                                    <TableCell className="fw-bold py-2">Dimensions</TableCell>
                                    <TableCell className="fw-bold py-2 text-center">In/Out</TableCell>
                                    <TableCell className="fw-bold py-2">Barcode Preview</TableCell>
                                    <TableCell className="fw-bold py-2 text-center">Labels</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isOrderItemDetails.map((item, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell className="fw-bold text-primary">{item.boxBarCode}</TableCell>
                                        <TableCell>{item.noOfItems}</TableCell>
                                        <TableCell>{item.weightKg ? `${item.weightKg} Kg` : '--'}</TableCell>
                                        <TableCell>{item.boxDimension || '--'}</TableCell>
                                        <TableCell className="text-center">
                                            <Box className="d-flex justify-content-center gap-1">
                                                {item.isScannedInn ? <CheckCircleIcon color="success" sx={{ fontSize: 18 }} /> : <CancelIcon color="error" sx={{ fontSize: 18 }} />}
                                                {item.isScannedOut ? <CheckCircleIcon color="success" sx={{ fontSize: 18 }} /> : <CancelIcon color="error" sx={{ fontSize: 18 }} />}
                                            </Box>
                                        </TableCell>
                                        <TableCell className="py-1">
                                            <div ref={(el) => (svgRefs.current[index] = el)} style={{ background: 'white', padding: '5px' }}>
                                                <Barcode value={item.boxBarCode} width={1.8} height={40} fontSize={10} margin={0} />
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <IconButton size="small" color="primary" onClick={() => handlePrint(item, index)}>
                                                <PrintIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Payment & Instructions Row */}
                    <Row className="g-3">
                        <Col lg={7}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    height: '100%',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '16px',
                                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                                    boxShadow: '0 4px 20px -2px rgba(148, 163, 184, 0.08)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '4px',
                                        height: '100%',
                                        background: 'linear-gradient(to bottom, #0284c7, #3b82f6)',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <PaymentIcon sx={{ fontSize: '1.25rem', mr: 1, color: '#475569' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                                        Payment & Billing
                                    </Typography>
                                </Box>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Box sx={{
                                            p: 2,
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '12px',
                                            bgcolor: '#f8fafc',
                                            mb: 2,
                                            '&:hover': { borderColor: '#cbd5e1' }
                                        }}>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 0.5, letterSpacing: '0.05em' }}>PAYMENT METHOD</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{order.paymentMethodInfo?.description || '--'}</Typography>
                                        </Box>
                                        <Box sx={{
                                            p: 2,
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '12px',
                                            bgcolor: '#f8fafc',
                                            '&:hover': { borderColor: '#cbd5e1' }
                                        }}>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 0.5, letterSpacing: '0.05em' }}>CHARGE BEARER</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{order.deliveryChargeBearerInfo?.deliveryChargeBearerName || '--'}</Typography>
                                        </Box>
                                    </Col>
                                    <Col md={6}>
                                        <Box sx={{
                                            bgcolor: '#f0f9ff',
                                            p: 2.5,
                                            borderRadius: '12px',
                                            border: '1px solid #bae6fd',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                                <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem' }}>Delivery Total Amount:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{order.orderSalesAmount || '0.00'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                                <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem' }}>Amount Paid:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{order.orderAmount || '0.00'}</Typography>
                                            </Box>
                                            <Divider sx={{ my: 1.5, borderColor: '#bae6fd' }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#0369a1', letterSpacing: '0.05em' }}>BALANCE TO COLLECT:</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0284c7' }}>{order.orderTotalAmount || '0.00'}</Typography>
                                            </Box>
                                        </Box>
                                    </Col>
                                </Row>
                            </Paper>
                        </Col>
                        <Col lg={5}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    height: '100%',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '12px',
                                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                                    boxShadow: '0 2px 12px -2px rgba(148, 163, 184, 0.06)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '4px',
                                        height: '100%',
                                        background: 'linear-gradient(to bottom, #10b981, #059669)',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <InfoIcon sx={{ fontSize: '1.1rem', mr: 0.75, color: '#475569' }} />
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                                        Special Instructions
                                    </Typography>
                                </Box>

                                {/* Shipping Instructions Section */}
                                <Box
                                    sx={{
                                        p: 1.5,
                                        borderRadius: '8px',
                                        border: '1px solid',
                                        borderColor: order.orderInstruction ? '#bfdbfe' : '#e2e8f0',
                                        backgroundColor: order.orderInstruction ? '#eff6ff' : '#f8fafc',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.04)',
                                            borderColor: order.orderInstruction ? '#3b82f6' : '#cbd5e1'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
                                        <LocalShippingIcon sx={{ fontSize: '0.95rem', color: order.deliveryInstruction ? '#2563eb' : '#64748b' }} />
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: order.orderInstruction ? '#1e3a8a' : '#475569' }}>
                                            Delivery Instructions
                                        </Typography>

                                    </Box>
                                    <Typography sx={{
                                        fontSize: '0.75rem',
                                        color: order.orderInstruction ? '#1e293b' : '#64748b',
                                        fontStyle: order.orderInstruction ? 'normal' : 'italic',
                                        lineHeight: 1.4,
                                        fontWeight: order.orderInstruction ? 500 : 400,
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere'
                                    }}>
                                        {order.orderInstruction || "No special instructions provided for this order."}
                                    </Typography>
                                </Box>

                                {/* Rider DLV Instructions Section */}
                               <Box
                                    sx={{
                                        p: 1.5,
                                        borderRadius: '8px',
                                        border: '1px solid',
                                        borderColor: order.riderDlvInst ? '#a7f3d0' : '#e2e8f0',
                                        backgroundColor: order.riderDlvInst ? '#ecfdf5' : '#f8fafc',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.04)',
                                            borderColor: order.riderDlvInst ? '#10b981' : '#cbd5e1'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
                                        <DirectionsBikeIcon sx={{ fontSize: '0.95rem', color: order.riderDlvInst ? '#059669' : '#64748b' }} />
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: order.riderDlvInst ? '#064e3b' : '#475569' }}>
                                            Rider Instructions
                                        </Typography>

                                    </Box>
                                    <Typography sx={{
                                        fontSize: '0.75rem',
                                        color: order.riderDlvInst ? '#1e293b' : '#64748b',
                                        fontStyle: order.riderDlvInst ? 'normal' : 'italic',
                                        lineHeight: 1.4,
                                        fontWeight: order.riderDlvInst ? 500 : 400,
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere'
                                    }}>
                                        {order.riderDlvInst || "No special instructions provided for this delivery."}
                                    </Typography>
                                </Box> 
                            </Paper>
                        </Col>
                    </Row>
                </Box>
            </ModalBody>
            <div className="modal__footer border-top-0 p-3 pt-0 text-center">
                <Typography variant="caption" className="text-muted">
                    This document is electronically generated for Informap Technology Center by PickNdrop.ae
                </Typography>
            </div>
        </Modal>
    );
};

export default connect((state) => ({
    rtl: state.rtl || false,
}))(OrderDetailsShippingViewDetailsModal);
